import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AppWindow,
  Clock3,
  Globe2,
  Laptop,
  Link2Off,
  MonitorSmartphone,
  RefreshCw,
  ShieldAlert,
  Smartphone,
  Trash2,
} from "lucide-react";

import { AccountDevice, OAuthConnection, SessionView, api, formatDate } from "../api";
import { AccountPageId } from "./AccountCenter";
import { EmptyState, ErrorState, LoadingState, MutationMessage, PageHeader, Section, StatusBadge, shortId } from "./components";

export function AccessPages({ page, onLoggedOut }: { page: AccountPageId; onLoggedOut: () => void }) {
  if (page === "devices") return <DevicePage />;
  if (page === "sessions") return <SessionPage onLoggedOut={onLoggedOut} />;
  return <ConnectionPage />;
}

function DevicePage() {
  const devices = useInfiniteQuery({
    queryKey: ["account-devices"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => api<{ devices: AccountDevice[]; nextCursor: string | null }>(`/api/v1/account/devices?limit=25${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ""}`),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const values = devices.data?.pages.flatMap((page) => page.devices) ?? [];
  return (
    <>
      <PageHeader eyebrow="NATIVE CLIENTS" title="客户端设备" description="查看通过设备协议绑定到账号的原生客户端设备。" />
      <div className="context-banner"><MonitorSmartphone /><div><strong>HWID 仅来自原生客户端 SDK</strong><span>网页不会采集、生成或上传 HWID；这里只显示不可逆摘要的简短提示。</span></div></div>
      <Section title="设备绑定" description="同一实体设备可在不同应用命名空间拥有独立标识。">
        {devices.isPending && <LoadingState />}
        {devices.error && <ErrorState error={devices.error} retry={() => void devices.refetch()} />}
        {!devices.isPending && values.length === 0 && <EmptyState icon={MonitorSmartphone} title="没有客户端设备" detail="在支持设备协议的原生客户端登录后，绑定会出现在这里。" />}
        <div className="item-list device-list">
          {values.map((device) => (
            <article className="list-item device-item" key={device.bindingId}>
              <span className="item-icon">{device.virtualEnvironment ? <Smartphone /> : <Laptop />}</span>
              <div className="item-main">
                <div className="item-title-row"><strong>{device.namespaceDisplayName}</strong><StatusBadge tone={device.trustLevel === "TRUSTED" ? "good" : "neutral"}>{device.trustLevel}</StatusBadge>{device.virtualEnvironment && <StatusBadge tone="warning">虚拟环境 {device.virtualConfidence ?? "-"}%</StatusBadge>}</div>
                <span>{device.organizationDisplayName} · {device.algorithm} · HWID {device.hwidHint}</span>
                <span>绑定 {formatDate(device.boundAt)} · 最近活动 {formatDate(device.lastSeenAt)}</span>
              </div>
              <span className="source-label">{device.bindingSource}</span>
            </article>
          ))}
        </div>
        {devices.hasNextPage && <div className="load-more"><button className="secondary-button" disabled={devices.isFetchingNextPage} type="button" onClick={() => void devices.fetchNextPage()}>{devices.isFetchingNextPage ? <RefreshCw className="spin" /> : <Clock3 />}载入更早设备</button></div>}
      </Section>
    </>
  );
}

function SessionPage({ onLoggedOut }: { onLoggedOut: () => void }) {
  const client = useQueryClient();
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: () => api<{ sessions: SessionView[] }>("/api/v1/sessions") });
  const revoke = useMutation({
    mutationFn: (session: SessionView) => api<void>(`/api/v1/sessions/${session.id}`, { method: "DELETE" }).then(() => session),
    onSuccess: (session) => {
      if (session.current) { client.clear(); onLoggedOut(); }
      else void client.invalidateQueries({ queryKey: ["sessions"] });
    },
  });
  const revokeOthers = useMutation({
    mutationFn: () => api<{ revoked: number }>("/api/v1/sessions/revoke-others", { method: "POST" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["sessions"] }),
  });
  const values = sessions.data?.sessions ?? [];
  return (
    <>
      <PageHeader eyebrow="ACCESS" title="登录会话" description="检查账号当前登录位置，并撤销不再使用的会话。" action={<button className="secondary-button" type="button" disabled={revokeOthers.isPending || values.length < 2} onClick={() => revokeOthers.mutate()}><ShieldAlert />撤销其他会话</button>} />
      <Section title="活动会话" description={`${values.length} 个登录会话`}>
        {sessions.isPending && <LoadingState />}
        {sessions.error && <ErrorState error={sessions.error} retry={() => void sessions.refetch()} />}
        <div className="item-list">
          {values.map((session) => (
            <article className="list-item" key={session.id}>
              <span className="item-icon"><Globe2 /></span>
              <div className="item-main">
                <div className="item-title-row"><strong>{session.userAgent ? browserLabel(session.userAgent) : "未知客户端"}</strong>{session.current && <StatusBadge tone="good">当前会话</StatusBadge>}</div>
                <span>{session.authenticationMethod} · {shortId(session.id)}</span>
                <span>最近活动 {formatDate(session.lastAccessedAt)} · 到期 {formatDate(session.expiresAt)}</span>
              </div>
              <button className={session.current ? "danger-button" : "icon-button danger"} type="button" title="撤销会话" aria-label={`撤销会话 ${shortId(session.id)}`} disabled={revoke.isPending} onClick={() => { if (window.confirm(session.current ? "撤销当前会话并退出登录？" : "撤销这个会话？")) revoke.mutate(session); }}>{session.current ? "退出此会话" : <Trash2 />}</button>
            </article>
          ))}
        </div>
        <MutationMessage error={revoke.error ?? revokeOthers.error} success={revokeOthers.data ? `已撤销 ${revokeOthers.data.revoked} 个其他会话` : false} />
      </Section>
    </>
  );
}

interface ConnectionEvent {
  id: string;
  connectionId: string;
  type: string;
  site: OAuthConnection["site"];
  scopes: string[];
  claims: string[];
  occurredAt: string;
}

function ConnectionPage() {
  const client = useQueryClient();
  const connections = useQuery({ queryKey: ["oauth-connections"], queryFn: () => api<{ connections: OAuthConnection[] }>("/api/v1/oauth/connections") });
  const history = useInfiniteQuery({
    queryKey: ["oauth-connection-events"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => api<{ events: ConnectionEvent[]; nextCursor: string | null }>(`/api/v1/oauth/connections/events?limit=20${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ""}`),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const revoke = useMutation({ mutationFn: (id: string) => api<void>(`/api/v1/oauth/connections/${id}`, { method: "DELETE" }), onSuccess: () => { void client.invalidateQueries({ queryKey: ["oauth-connections"] }); void client.invalidateQueries({ queryKey: ["oauth-connection-events"] }); } });
  const values = connections.data?.connections ?? [];
  const events = history.data?.pages.flatMap((page) => page.events) ?? [];
  return (
    <>
      <PageHeader eyebrow="CONSENT" title="连接应用" description="查看使用 EvilEyeAuth 登录过的站点、授权范围和使用记录。" />
      <Section title="当前连接" description="撤销连接会同时撤销该应用的活动授权和令牌。">
        {connections.isPending && <LoadingState />}
        {connections.error && <ErrorState error={connections.error} retry={() => void connections.refetch()} />}
        {!connections.isPending && values.length === 0 && <EmptyState icon={AppWindow} title="没有活动连接" detail="通过 EvilEyeAuth 登录第三方站点后，授权会显示在这里。" />}
        <div className="item-list">
          {values.map((connection) => (
            <article className="list-item connection-item" key={connection.id}>
              <span className="item-icon"><AppWindow /></span>
              <div className="item-main"><strong>{connection.site.applicationDisplayName}</strong><span>{connection.site.organizationDisplayName} · {connection.site.environmentDisplayName}</span><div className="scope-list">{connection.scopes.map((scope) => <StatusBadge key={scope}>{scope}</StatusBadge>)}</div><span>连接于 {formatDate(connection.connectedAt)} · 最近使用 {formatDate(connection.lastUsedAt)}</span></div>
              <button className="danger-button" disabled={revoke.isPending} type="button" onClick={() => { if (window.confirm(`撤销 ${connection.site.applicationDisplayName} 的连接？`)) revoke.mutate(connection.id); }}><Link2Off />撤销</button>
            </article>
          ))}
        </div>
        <MutationMessage error={revoke.error} success={revoke.isSuccess && "应用连接已撤销"} />
      </Section>
      <Section title="连接历史" description="授权、访问、权限变化和撤销事件只追加保存。">
        {history.isPending && <LoadingState />}
        {history.error && <ErrorState error={history.error} />}
        {events.length === 0 && !history.isPending && <EmptyState icon={Clock3} title="暂无连接记录" detail="连接活动会按时间显示在这里。" />}
        <ol className="event-timeline">
          {events.map((event) => <li key={event.id}><span /><div><strong>{event.site.applicationDisplayName}</strong><small>{event.type}</small></div><time>{formatDate(event.occurredAt)}</time></li>)}
        </ol>
        {history.hasNextPage && <div className="load-more"><button className="secondary-button" disabled={history.isFetchingNextPage} type="button" onClick={() => void history.fetchNextPage()}><Clock3 />载入更早记录</button></div>}
      </Section>
    </>
  );
}

function browserLabel(userAgent: string) {
  if (/Edg\//.test(userAgent)) return "Microsoft Edge";
  if (/Firefox\//.test(userAgent)) return "Firefox";
  if (/Chrome\//.test(userAgent)) return "Chrome";
  if (/Safari\//.test(userAgent)) return "Safari";
  return userAgent.slice(0, 60);
}
